import { useTranslation } from "react-i18next";
import {
  Buildings, ShieldCheck, Certificate, Lock, EnvelopeSimple,
  GooglePlayLogo, AppleLogo, CheckCircle, XCircle, Link, MapPin,
} from "@phosphor-icons/react";
import SEOHead from "../components/SEOHead";
import { useNavigation } from "../context/NavigationContext";

const Row = ({ Icon, label, value, href }) => (
  <div className="flex items-start gap-3 py-3 border-b border-white/5" data-testid={`trust-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Icon size={20} weight="duotone" className="text-lumina-violet flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-white hover:text-lumina-violet transition-colors text-sm break-all">
          {value}
        </a>
      ) : (
        <div className="text-white text-sm">{value}</div>
      )}
    </div>
  </div>
);

const TrustSecurityPage = () => {
  const { t } = useTranslation();
  const { navigateTo } = useNavigation();

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="trust-security-page">
      <SEOHead
        title={`${t("trustSecurity.title")} | Lumina Wallet`}
        description={t("trustSecurity.meta")}
        path="/trust-security"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-lumina-violet/10 border border-lumina-violet/30 rounded-full text-sm text-lumina-violet mb-4">
            <ShieldCheck size={16} weight="fill" />
            <span>{t("trustSecurity.badge")}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            {t("trustSecurity.title")}
          </h1>
          <p className="text-gray-400 text-base max-w-2xl mx-auto">
            {t("trustSecurity.intro")}
          </p>
        </div>

        {/* Section 1 — Company Identity */}
        <section className="mb-10 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Buildings size={24} weight="duotone" className="text-lumina-violet" />
            {t("trustSecurity.company.title")}
          </h2>
          <p className="text-gray-400 text-sm mb-4">{t("trustSecurity.company.desc")}</p>
          <Row Icon={Buildings} label={t("trustSecurity.company.name")} value="HÉDONISME SARL" />
          <Row Icon={Certificate} label="SIREN" value="901 439 505" href="https://annuaire-entreprises.data.gouv.fr/entreprise/hedonisme-901439505" />
          <Row Icon={MapPin} label={t("trustSecurity.company.country")} value={t("trustSecurity.company.france")} />
          <Row Icon={CheckCircle} label={t("trustSecurity.company.form")} value={t("trustSecurity.company.sarl")} />
        </section>

        {/* Section 2 — Official Stores */}
        <section className="mb-10 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <CheckCircle size={24} weight="duotone" className="text-green-400" />
            {t("trustSecurity.stores.title")}
          </h2>
          <p className="text-gray-400 text-sm mb-4">{t("trustSecurity.stores.desc")}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <a
              href="https://apps.apple.com/us/app/lumina-wallet/id6760651380"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl bg-black border border-white/20 hover:border-white/40 transition-all"
              data-testid="trust-appstore"
            >
              <AppleLogo size={28} weight="fill" className="text-white" />
              <div>
                <div className="text-xs text-gray-400">{t("trustSecurity.stores.publisher")} HÉDONISME</div>
                <div className="text-white font-medium">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.luminawallet.app"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-xl bg-black border border-white/20 hover:border-white/40 transition-all"
              data-testid="trust-googleplay"
            >
              <GooglePlayLogo size={28} weight="fill" className="text-[#01875F]" />
              <div>
                <div className="text-xs text-gray-400">{t("trustSecurity.stores.publisher")} HÉDONISME</div>
                <div className="text-white font-medium">Google Play</div>
              </div>
            </a>
          </div>
        </section>

        {/* Section 3 — Technical Security */}
        <section className="mb-10 p-6 rounded-2xl bg-white/5 border border-white/10">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Lock size={24} weight="duotone" className="text-lumina-violet" />
            {t("trustSecurity.tech.title")}
          </h2>
          <p className="text-gray-400 text-sm mb-4">{t("trustSecurity.tech.desc")}</p>
          <Row Icon={CheckCircle} label="HTTPS / TLS" value={t("trustSecurity.tech.tls")} href="https://www.ssllabs.com/ssltest/analyze.html?d=www.luminawallet.org" />
          <Row Icon={CheckCircle} label="DNSSEC" value={t("trustSecurity.tech.dnssec")} />
          <Row Icon={CheckCircle} label="DMARC / SPF" value={t("trustSecurity.tech.dmarc")} />
          <Row Icon={CheckCircle} label={t("trustSecurity.tech.threat")} value={t("trustSecurity.tech.threatVal")} href="https://www.virustotal.com/gui/domain/luminawallet.org" />
          <Row Icon={Link} label="security.txt" value="/.well-known/security.txt" href="/.well-known/security.txt" />
        </section>

        {/* Section 4 — What Lumina IS / IS NOT */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <section className="p-6 rounded-2xl bg-green-500/5 border border-green-500/20">
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <CheckCircle size={22} weight="duotone" className="text-green-400" />
              {t("trustSecurity.isIsNot.isTitle")}
            </h3>
            <ul className="space-y-2 text-sm text-gray-300">
              {t("trustSecurity.isIsNot.is", { returnObjects: true }).map((item, i) => (
                <li key={item} className="flex gap-2"><span className="text-green-400">✓</span>{item}</li>
              ))}
            </ul>
          </section>
          <section className="p-6 rounded-2xl bg-red-500/5 border border-red-500/20">
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <XCircle size={22} weight="duotone" className="text-red-400" />
              {t("trustSecurity.isIsNot.isNotTitle")}
            </h3>
            <ul className="space-y-2 text-sm text-gray-300">
              {t("trustSecurity.isIsNot.isNot", { returnObjects: true }).map((item, i) => (
                <li key={item} className="flex gap-2"><span className="text-red-400">✗</span>{item}</li>
              ))}
            </ul>
          </section>
        </div>

        {/* Section 5 — Security Contact */}
        <section className="mb-10 p-6 rounded-2xl bg-lumina-violet/10 border border-lumina-violet/30">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <EnvelopeSimple size={24} weight="duotone" className="text-lumina-violet" />
            {t("trustSecurity.contact.title")}
          </h2>
          <p className="text-gray-300 text-sm mb-4">{t("trustSecurity.contact.desc")}</p>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:security@luminawallet.org"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-lumina-violet text-white text-sm font-medium hover:bg-lumina-violet/90 transition-all"
              data-testid="trust-security-email"
            >
              <EnvelopeSimple size={18} weight="fill" />
              security@luminawallet.org
            </a>
            <a
              href="mailto:support@luminawallet.org"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-all"
              data-testid="trust-support-email"
            >
              <EnvelopeSimple size={18} weight="fill" />
              support@luminawallet.org
            </a>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="p-5 rounded-xl bg-white/5 border border-white/10 text-xs text-gray-500 leading-relaxed">
          {t("footer.disclaimer")}
        </div>

        {/* Back home CTA */}
        <div className="text-center mt-10">
          <button
            onClick={() => navigateTo("home")}
            className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all text-sm"
            data-testid="trust-back-home"
          >
            ← {t("trustSecurity.backHome")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrustSecurityPage;
